import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Gauge, Loader2, RefreshCw } from "lucide-react";

const CATEGORY_ORDER = ["AI", "SEO", "Email", "Messaging", "Telephony",
  "Security", "Finance", "Domains", "Websites", "Client OAuth"];

const fmt = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString("en-AU");
  return String(v);
};

export default function ApiUsagePage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (live = false) => {
    setBusy(true);
    try {
      const { data } = await api.get("/api-usage/overview",
        { params: live ? { live: true } : {} });
      setData(data);
      if (live) toast.success("Live balances refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load API usage");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = {};
  (data?.services || []).forEach((s) => {
    grouped[s.category] = grouped[s.category] || [];
    grouped[s.category].push(s);
  });
  const cats = [...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c))];

  const connected = (data?.services || []).filter((s) => s.connected).length;

  return (
    <div className="space-y-5" data-testid="api-usage-page">
      <div className="flex items-center gap-3">
        <Gauge className="h-6 w-6 text-[#002FA7]" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">API Usage</h1>
          <p className="text-sm text-zinc-500">
            Every connected API, its quota and usage for {data?.period || "the current period"}.
          </p>
        </div>
        <Button variant="outline" onClick={() => load(true)} disabled={busy}
          data-testid="api-usage-live-refresh-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh live balances
        </Button>
      </div>

      {!data ? <Loader2 className="h-6 w-6 animate-spin text-zinc-400" /> : (
        <>
          <div className="text-sm text-zinc-500" data-testid="api-usage-summary">
            <b>{connected}</b> of {data.services.length} services connected
          </div>
          {cats.map((cat) => (
            <Card key={cat}>
              <CardContent className="p-0">
                <div className="px-4 py-2 bg-zinc-50 border-b text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  {cat}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-zinc-500">
                      <th className="p-3">Service</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Detail</th>
                      <th className="p-3 text-right">Quota</th>
                      <th className="p-3 text-right">Used</th>
                      <th className="p-3 text-right">Remaining</th>
                      <th className="p-3">Last verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[cat].map((s) => (
                      <tr key={s.key} className="border-b last:border-0"
                        data-testid={`api-usage-row-${s.key}`}>
                        <td className="p-3 font-medium">{s.name}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={s.connected
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-zinc-100 text-zinc-500 border-zinc-200"}>
                            {s.connected ? "Connected" : "Not configured"}
                          </Badge>
                        </td>
                        <td className="p-3 text-zinc-500 text-xs max-w-xs">{s.detail}</td>
                        <td className="p-3 text-right">{fmt(s.quota)}</td>
                        <td className="p-3 text-right">
                          {fmt(s.used)}{s.used != null && s.unit ? <span className="text-xs text-zinc-400"> {s.unit}</span> : ""}
                        </td>
                        <td className="p-3 text-right">
                          {fmt(s.remaining)}{s.remaining != null && s.unit && typeof s.remaining === "number"
                            ? <span className="text-xs text-zinc-400"> {s.unit}</span> : ""}
                        </td>
                        <td className="p-3 text-xs text-zinc-400">
                          {s.last_verified_at ? String(s.last_verified_at).slice(0, 16).replace("T", " ") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}
